import { initLiveChatSDK } from './initLiveChatSDK'
import { setupChatEventsHandlers } from './setupChatEventsHandlers'
import { getWelcomeMessageForGroup } from './getWelcomeMsgForGroups'
import { saveChatSession } from './saveChatSession'
import { getChatSession } from './getChatSession'
import { sendMessage } from './sendMessage'

/** @type {import('@livechat/customer-sdk').CustomerSDK | null} */
let sdkInstance = null

/**
 * @param {string} url - The redirect URL from form submission
 * @returns {{ groupId: string, chatParams: Record<string, any> }}
 */
function extractChatDataFromUrl(url) {
  const defaultResult = {
    groupId: '13',
    chatParams: { NOTE: 'NOT IN VS', stack: 'funnels' },
  }

  try {
    const chatUrl = new URL(url)
    const urlParams = chatUrl.searchParams

    let groupId = '13'
    /** @type {Record<string, any>} */
    let chatParams = { NOTE: 'NOT IN VS' }

    const lParam = urlParams.get('l')
    if (!lParam) {
      return defaultResult
    }

    try {
      const decodedUrl = atob(lParam)

      // Extract group ID
      const groupsMatch = decodedUrl.match(/groups=([^&]*)/)
      if (groupsMatch) {
        groupId = groupsMatch[1]
      }

      // Extract params
      const paramsMatch = decodedUrl.match(/params=([^&]*)/)
      if (paramsMatch) {
        chatParams = Object.fromEntries(
          new URLSearchParams(decodeURIComponent(paramsMatch[1])).entries(),
        )
      }

      // Extract uuid
      const uuidMatch = decodedUrl.match(/uuid=([^&]*)/)
      if (uuidMatch) {
        chatParams.uuid = uuidMatch[1]
      }

      chatParams.chatVersion = 'Embedded Chat - Funnels'
    } catch (e) {
      console.warn('Error decoding base64 URL parameter:', e)
      return defaultResult
    }

    chatParams.stack = 'funnels'

    return { groupId, chatParams }
  } catch (e) {
    console.warn('Error parsing redirect URL:', e)
    return defaultResult
  }
}

export function showEmbeddedChatLoading() {
  const formSection = document.getElementById('form-section')
  const chatSection = document.getElementById('embedded-chat-section')
  const loadingState = document.getElementById('embedded-chat-loading')

  if (formSection) formSection.style.display = 'none'
  if (chatSection) chatSection.style.display = 'block'
  if (loadingState) loadingState.style.display = 'block'

  window.scrollTo(0, 0)
}

/**
 * Transition from loading screen to chat
 */
function setChatActive() {
  const loadingState = document.getElementById('embedded-chat-loading')
  const activeState = document.getElementById('embedded-chat-active')

  if (loadingState) {
    loadingState.style.display = 'none'
  }

  if (activeState) {
    activeState.style.display = 'block'
  }

  window.scrollTo(0, 0)
}

function setupChatInputHandlers() {
  const sendButton = document.getElementById('send-button')
  const messageInput = /** @type {HTMLInputElement | null} */ (
    document.getElementById('message-input')
  )

  if (!sendButton || !messageInput) {
    return
  }

  const handleSend = () => {
    if (!sdkInstance) {
      console.error('unexpected: chat sdk not found')
      return
    }
    const messageText = messageInput.value.trim()
    if (messageText) {
      sendMessage(sdkInstance, messageText)
    }
  }

  sendButton.addEventListener('click', (e) => {
    e.preventDefault()
    handleSend()
  })

  messageInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  })

  // Handle send button active state
  messageInput.addEventListener('input', () => {
    const hasText = messageInput.value.trim().length > 0
    if (hasText) {
      sendButton.classList.add('active')
    } else {
      sendButton.classList.remove('active')
    }
  })
}

/**
 * @param {{ redirectUrl: string, fallbackUrl: string }} options
 */
export async function initEmbeddedChatFromResponse(options) {
  const { redirectUrl, fallbackUrl } = options

  // Check for chat URL
  if (!redirectUrl || !/chat/i.test(redirectUrl)) {
    window.location.href = redirectUrl || fallbackUrl
    return
  }

  try {
    const { groupId, chatParams } = extractChatDataFromUrl(redirectUrl)
    const groupIdNum = parseInt(groupId, 10)
    const welcomeMessage = getWelcomeMessageForGroup(groupId)

    // Store chatParams in sessionStorage before SDK setup to survive reconnects
    sessionStorage.setItem('embeddedChatParams', JSON.stringify(chatParams))

    const session = getChatSession()
    saveChatSession({
      ...session,
      groupId: groupIdNum,
      welcomeMessage,
    })

    sdkInstance = initLiveChatSDK(groupIdNum)

    if (!sdkInstance) {
      throw new Error('Live chat SDK could not be initialized')
    }

    // Pass chatParams to setupChatEventsHandlers so handleChatConnected uses correct params
    setupChatEventsHandlers(sdkInstance, chatParams)
    setupChatInputHandlers()

    // Listen for connected event to transition UI to active chat state
    sdkInstance.on('connected', () => {
      setChatActive()
    })

    sdkInstance.connect()
  } catch (error) {
    console.error('Embedded chat initialization error:', error)
    window.location.href = fallbackUrl
  }
}
